/**
 * MyCopro - Services Store
 * Gestion des todos et services de la résidence
 * ✅ Accès réservé aux membres du Conseil Syndical
 */

import { create } from 'zustand';
import {
  collection,
  doc,
  addDoc,
  updateDoc,
  deleteDoc,
  query,
  orderBy,
  onSnapshot,
  Timestamp,
  arrayUnion
} from 'firebase/firestore';
import { db } from '@/config/firebase';

// ============================================================================
// TYPES
// ============================================================================

export type TodoPriority = 'high' | 'medium' | 'low';
export type TodoDeadline = 'this_week' | 'this_month' | 'one_month' | 'three_months' | 'before_ag';
export type TodoStatus = 'pending' | 'completed';

export interface TodoComment {
  id: string;
  authorId: string;
  authorName: string;
  text: string;
  createdAt: string;
}

export interface Todo {
  id: string;
  title: string;
  description?: string;
  priority: TodoPriority;
  deadline: TodoDeadline;
  status: TodoStatus;
  comments: TodoComment[];
  createdBy: string;
  createdByName: string;
  createdAt: string;
  completedAt?: string;
  completedBy?: string;
  completedByName?: string;
}

export type ServiceType = 'elevator' | 'plumber' | 'electrician' | 'heating' | 'other';

export interface Service {
  id: string;
  name: string;
  phone: string;
  type: ServiceType;
  createdBy: string;
  createdAt: string;
}

// ============================================================================
// CONSTANTS
// ============================================================================

export const PRIORITY_CONFIG: Record<TodoPriority, { label: string; color: string; bgColor: string }> = {
  high: { label: 'Haute', color: 'text-red-600', bgColor: 'bg-red-100' },
  medium: { label: 'Moyenne', color: 'text-orange-600', bgColor: 'bg-orange-100' },
  low: { label: 'Basse', color: 'text-green-600', bgColor: 'bg-green-100' }
};

export const DEADLINE_CONFIG: Record<TodoDeadline, { label: string; shortLabel: string }> = {
  this_week: { label: 'Cette semaine', shortLabel: 'Semaine' },
  this_month: { label: 'Ce mois', shortLabel: 'Mois' },
  one_month: { label: 'Sous 1 mois', shortLabel: '1 mois' },
  three_months: { label: 'Sous 3 mois', shortLabel: '3 mois' },
  before_ag: { label: 'Avant AG', shortLabel: 'Avant AG' }
};

export const SERVICE_TYPE_CONFIG: Record<ServiceType, { label: string; icon: string; color: string }> = {
  elevator: { label: 'Ascenseur', icon: '🛗', color: 'bg-red-100 text-red-600' },
  plumber: { label: 'Plomberie', icon: '🔧', color: 'bg-blue-100 text-blue-600' },
  electrician: { label: 'Électricité', icon: '⚡', color: 'bg-yellow-100 text-yellow-600' },
  heating: { label: 'Chauffage', icon: '🔥', color: 'bg-orange-100 text-orange-600' },
  other: { label: 'Autre', icon: '🛠️', color: 'bg-gray-100 text-gray-600' }
};

// ============================================================================
// STORE
// ============================================================================

interface ServicesState {
  // Data
  todos: Todo[];
  services: Service[];
  
  // Loading
  isLoading: boolean;
  isSubmitting: boolean;
  error: string | null;
  
  // Subscriptions
  unsubscribeTodos: (() => void) | null;
  unsubscribeServices: (() => void) | null;
  
  // Actions - Subscriptions
  subscribeToTodos: (residenceId: string) => void;
  subscribeToServices: (residenceId: string) => void;
  unsubscribeAll: () => void;
  
  // Actions - Todos
  addTodo: (residenceId: string, todo: Omit<Todo, 'id' | 'createdAt' | 'comments'>) => Promise<string | null>;
  updateTodo: (residenceId: string, todoId: string, data: Partial<Todo>) => Promise<boolean>;
  deleteTodo: (residenceId: string, todoId: string) => Promise<boolean>;
  completeTodo: (residenceId: string, todoId: string, userId: string, userName: string) => Promise<boolean>;
  reopenTodo: (residenceId: string, todoId: string) => Promise<boolean>;
  addComment: (residenceId: string, todoId: string, comment: Omit<TodoComment, 'id' | 'createdAt'>) => Promise<boolean>;
  
  // Actions - Services
  addService: (residenceId: string, service: Omit<Service, 'id' | 'createdAt'>) => Promise<string | null>;
  updateService: (residenceId: string, serviceId: string, data: Partial<Service>) => Promise<boolean>;
  deleteService: (residenceId: string, serviceId: string) => Promise<boolean>;
}

export const useServicesStore = create<ServicesState>((set, get) => ({
  todos: [],
  services: [],
  isLoading: false,
  isSubmitting: false,
  error: null,
  unsubscribeTodos: null,
  unsubscribeServices: null,

  // ============================================================================
  // SUBSCRIPTIONS
  // ============================================================================

  subscribeToTodos: (residenceId: string) => {
    const { unsubscribeTodos } = get();
    if (unsubscribeTodos) unsubscribeTodos();
    
    if (!residenceId) {
      set({ todos: [], unsubscribeTodos: null });
      return;
    }

    set({ isLoading: true });

    const todosRef = collection(db, 'residences', residenceId, 'todos');
    const q = query(todosRef, orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, 
      (snapshot) => {
        const todosData: Todo[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          todosData.push({
            id: docSnap.id,
            title: data.title || '',
            description: data.description || '',
            priority: data.priority || 'medium',
            deadline: data.deadline || 'this_month',
            status: data.status || 'pending',
            comments: data.comments || [],
            createdBy: data.createdBy || '',
            createdByName: data.createdByName || '',
            createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString(),
            completedAt: data.completedAt?.toDate?.().toISOString(),
            completedBy: data.completedBy,
            completedByName: data.completedByName
          });
        });
        set({ todos: todosData, isLoading: false, error: null });
      },
      (error) => {
        console.error('[ServicesStore] Error subscribing to todos:', error);
        set({ isLoading: false, error: 'Erreur de chargement des tâches' });
      }
    );

    set({ unsubscribeTodos: unsub });
  },

  subscribeToServices: (residenceId: string) => {
    const { unsubscribeServices } = get();
    if (unsubscribeServices) unsubscribeServices();
    
    if (!residenceId) {
      set({ services: [], unsubscribeServices: null });
      return;
    }

    const servicesRef = collection(db, 'residences', residenceId, 'services');
    const q = query(servicesRef, orderBy('createdAt', 'desc'));

    const unsub = onSnapshot(q, 
      (snapshot) => {
        const servicesData: Service[] = [];
        snapshot.forEach((docSnap) => {
          const data = docSnap.data();
          servicesData.push({
            id: docSnap.id,
            name: data.name || '',
            phone: data.phone || '',
            type: data.type || 'other',
            createdBy: data.createdBy || '',
            createdAt: data.createdAt?.toDate?.().toISOString() || new Date().toISOString()
          });
        });
        set({ services: servicesData });
      },
      (error) => {
        console.error('[ServicesStore] Error subscribing to services:', error);
      }
    );

    set({ unsubscribeServices: unsub });
  },

  unsubscribeAll: () => {
    const { unsubscribeTodos, unsubscribeServices } = get();
    if (unsubscribeTodos) unsubscribeTodos();
    if (unsubscribeServices) unsubscribeServices();
    set({ 
      todos: [], 
      services: [], 
      unsubscribeTodos: null, 
      unsubscribeServices: null 
    });
  },

  // ============================================================================
  // TODOS ACTIONS
  // ============================================================================

  addTodo: async (residenceId, todoData) => {
    if (!residenceId) return null;
    
    set({ isSubmitting: true });
    try {
      const todosRef = collection(db, 'residences', residenceId, 'todos');
      const docRef = await addDoc(todosRef, {
        ...todoData,
        comments: [],
        createdAt: Timestamp.now()
      });
      set({ isSubmitting: false });
      return docRef.id;
    } catch (error) {
      console.error('[ServicesStore] Error adding todo:', error);
      set({ isSubmitting: false, error: 'Erreur lors de la création' });
      return null;
    }
  },

  updateTodo: async (residenceId, todoId, data) => {
    if (!residenceId || !todoId) return false;
    
    set({ isSubmitting: true });
    try {
      const todoRef = doc(db, 'residences', residenceId, 'todos', todoId);
      await updateDoc(todoRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
      set({ isSubmitting: false });
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error updating todo:', error);
      set({ isSubmitting: false });
      return false;
    }
  },

  deleteTodo: async (residenceId, todoId) => {
    if (!residenceId || !todoId) return false;
    
    try {
      const todoRef = doc(db, 'residences', residenceId, 'todos', todoId);
      await deleteDoc(todoRef);
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error deleting todo:', error);
      return false;
    }
  },

  completeTodo: async (residenceId, todoId, userId, userName) => {
    if (!residenceId || !todoId) return false;
    
    try {
      const todoRef = doc(db, 'residences', residenceId, 'todos', todoId);
      await updateDoc(todoRef, {
        status: 'completed',
        completedAt: Timestamp.now(),
        completedBy: userId,
        completedByName: userName
      });
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error completing todo:', error);
      return false;
    }
  },

  reopenTodo: async (residenceId, todoId) => {
    if (!residenceId || !todoId) return false;
    
    try {
      const todoRef = doc(db, 'residences', residenceId, 'todos', todoId);
      await updateDoc(todoRef, {
        status: 'pending',
        completedAt: null,
        completedBy: null,
        completedByName: null
      });
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error reopening todo:', error);
      return false;
    }
  },

  addComment: async (residenceId, todoId, commentData) => {
    if (!residenceId || !todoId) return false;
    
    try {
      const todoRef = doc(db, 'residences', residenceId, 'todos', todoId);
      const newComment: TodoComment = {
        ...commentData,
        id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
        createdAt: new Date().toISOString()
      };
      await updateDoc(todoRef, {
        comments: arrayUnion(newComment)
      });
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error adding comment:', error);
      return false;
    }
  },

  // ============================================================================
  // SERVICES ACTIONS
  // ============================================================================

  addService: async (residenceId, serviceData) => {
    if (!residenceId) return null;
    
    set({ isSubmitting: true });
    try {
      const servicesRef = collection(db, 'residences', residenceId, 'services');
      const docRef = await addDoc(servicesRef, {
        ...serviceData,
        createdAt: Timestamp.now()
      });
      set({ isSubmitting: false });
      return docRef.id;
    } catch (error) {
      console.error('[ServicesStore] Error adding service:', error);
      set({ isSubmitting: false });
      return null;
    }
  },

  updateService: async (residenceId, serviceId, data) => {
    if (!residenceId || !serviceId) return false;
    
    set({ isSubmitting: true });
    try {
      const serviceRef = doc(db, 'residences', residenceId, 'services', serviceId);
      await updateDoc(serviceRef, {
        ...data,
        updatedAt: Timestamp.now()
      });
      set({ isSubmitting: false });
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error updating service:', error);
      set({ isSubmitting: false });
      return false;
    }
  },

  deleteService: async (residenceId, serviceId) => {
    if (!residenceId || !serviceId) return false;
    
    try {
      const serviceRef = doc(db, 'residences', residenceId, 'services', serviceId);
      await deleteDoc(serviceRef);
      return true;
    } catch (error) {
      console.error('[ServicesStore] Error deleting service:', error);
      return false;
    }
  }
}));

export default useServicesStore;
